import React, { useEffect, useState } from "react";
import { Button, Select, Space, Col, Divider } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import styles from "./add-edit-subject.module.css";
import CustomDrawer from "../../../../../common/custom-drawer/custom-drawer";
import CustomSelect from "../../../../../common/input/custom-select/custom-select";
import CustomButton from "../../../../../common/custom-button/custom-button";
import { addSubjects, fetchSubjectById, fetchSubjectTypes, updateSubject } from "../../../../../services/subject";
import CustomTextfield from "../../../../../common/input/custom-textfield/custom-textfield";
import { fetchClass } from "../../../../../services/student";
import { fetchTeacherList } from "../../../../../services/teachers";
import CustomTypableSelect from "../../../../../common/input/custom-typable-select/custom-typable-select";

const { Option } = Select;

const AddEditSubject = ({ open, onClose, editSubjectData }) => {
  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      subjectType: "",
      subjectSets: [
        {
          subject: "",
          forms: [],
          teachers: [],
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "subjectSets",
  });
  
  const [loading, setLoading] = useState(false);
  const [subjectTypes, setSubjectTypes] = useState([]);
  const [classList, setClassList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);

  useEffect(() => {
    getSubjectTypes();
    getClassList();
    getTeacherList();
  }, []);

  useEffect(() => {
    if (editSubjectData?.id) {
      setSubjectDetails(editSubjectData?.id);
    }
  }, [editSubjectData, reset]);

  const setSubjectDetails = async (id) => {
    try {
      setLoading(true);
      const response = await fetchSubjectById(id);
      if (response?.status) {
        const data = response?.data?.data?.subject;
        reset({
          subjectType: data?.department?.name,
          subjectSets: [
            {
              subject: data?.name,
              forms: Array.from(new Set(data?.level_ids ?? [])),
              teachers: Array.from(new Set(data?.subjectTeachers?.map(item => item?.teacher_id) ?? [])),
            }
          ]
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const getSubjectTypes = async () => {
    try {
      setLoading(true);
      const response = await fetchSubjectTypes();
      if (response?.status) {
        setSubjectTypes(response?.data?.data?.departments);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const getClassList = async () => {
    try {
      setLoading(true);
      const response = await fetchClass();
      if (response?.status) {
        setClassList(response?.data?.data?.levels);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };
  
  const getTeacherList = async () => {
    try {
      setLoading(true);
      const response = await fetchTeacherList();
      if (response?.status) {
        setTeacherList(response?.data?.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        department_name: data?.subjectType,
        subjects: data?.subjectSets?.map(item => ({
          name: item?.subject,
          level_ids: item?.forms,
          teacher_ids: item?.teachers
        }))
      };

      if (editSubjectData?.id) {
        await updateSubject(editSubjectData?.id, payload);
      } else {
        await addSubjects(payload);
      }
      onClose();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <CustomDrawer
      title={`${editSubjectData?.id ? 'Edit' : 'Add'} Subject`}
      open={open}
      onClose={onClose}
      className={styles.drawer}
      width="50vw"
      drawerStyles={{ body: { padding: "24px" } }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formSectionBox}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <div className={styles.add_subject}>
              <div>
                <label
                  style={{
                    fontWeight: 500,
                    marginBottom: 6,
                    display: "inline-block",
                  }}
                >
                  Subject Type<span style={{ color: "red" }}>*</span>
                </label>
                <Controller
                  name="subjectType"
                  control={control}
                  rules={{ required: "Subject Type is required" }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomTypableSelect
                      labelRowor="#171717"
                      placeholder="--Select--"
                      showSearch
                      size="large"
                      value={value}
                      onChange={onChange}
                      status={error ? "error" : ""}
                      filterOption={(input, option) =>
                        option?.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      style={{ width: "210px" }}
                      defaultOptions={subjectTypes?.map(item=> item?.name)}
                    />
                  )}
                />
              </div>

              {!editSubjectData?.id ? (
                <CustomButton
                  title="Add Subject"
                  type="text"
                  icon={
                    <div className={styles.iconCircle}>
                      <PlusOutlined />
                    </div>
                  }
                  className={styles.addSubjectBtn}
                  showTooltip={false}
                  onClick={() => append({ subject: "", forms: [], teachers: [] })}
                />
              ) : null}
            </div>

            {fields?.map((field, index) => (
              <Space 
                direction="vertical" 
                style={{ width: "100%" }} 
                size="large" 
                key={field?.id}
              >
                {fields?.length > 1 && (
                  <div>
                    <Divider
                      style={{
                        backgroundColor: "#d9d9d9",
                        marginTop: 8,
                      }}
                    />
                    <div className={styles.delete}>
                      <CustomButton
                        title="Delete"
                        type="text"
                        icon={<DeleteOutlined />}
                        className={styles.deleteSubjectBtn}
                        showTooltip={false}
                        onClick={() => remove(index)}
                      />
                    </div>
                  </div>
                )}

                <Col span={24}>
                  <label
                    style={{
                      fontWeight: 500,
                      marginBottom: 6,
                      display: "inline-block",
                    }}
                  >
                    Subject Name<span style={{ color: "red" }}>*</span>
                  </label>
                  <Controller
                    name={`subjectSets.${index}.subject`}
                    control={control}
                    rules={{ required: "Subject is required" }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomTextfield
                        id={`subjectSets.${index}.subject`}
                        placeholder="Enter subject"
                        size="large"
                        value={value}
                        onChange={onChange}
                        status={error ? "error" : ""}
                      />
                    )}
                  />
                </Col>

                <Col span={24}>
                  <label
                    style={{
                      fontWeight: 500,
                      marginBottom: 6,
                      display: "inline-block",
                    }}
                  >
                    Form Name (Optional)
                  </label>
                  <Controller
                    name={`subjectSets.${index}.forms`}
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomSelect
                        labelColor="#171717"
                        placeholder="--Select--"
                        showSearch
                        size="large"
                        mode="multiple"
                        value={value}
                        onChange={onChange}
                        status={error ? "error" : ""}
                      >
                        {classList?.map((option, index) => (
                          <Option key={`${option?.id}-${index}`} value={option?.id}>
                            {option?.name}
                          </Option>
                        ))}
                      </CustomSelect>
                    )}
                  />
                </Col>

                <Col span={24}>
                  <label
                    style={{
                      fontWeight: 500,
                      marginBottom: 6,
                      display: "inline-block",
                    }}
                  >
                    Teacher Name (Optional)
                  </label>
                  <Controller
                    name={`subjectSets.${index}.teachers`}
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomSelect
                        labelColor="#171717"
                        placeholder="--Select--"
                        showSearch
                        size="large"
                        mode="multiple"
                        value={value}
                        onChange={onChange}
                        status={error ? "error" : ""}
                      >
                        {teacherList?.map((option, index) => (
                          <Option key={`${option?.id}-${index}`} value={option?.id}>
                            {option?.full_name}
                          </Option>
                        ))}
                      </CustomSelect>
                    )}
                  />
                </Col>
              </Space>
            ))}
          </Space>
        </div>

        <div className={styles.formActions}>
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                background: "rgba(57, 181, 74, 1)",
                borderColor: "rgba(57, 181, 74, 1)",
              }}
            >
              Save
            </Button>
          </Space>
        </div>
      </form>
    </CustomDrawer>
  );
};

export default AddEditSubject;
